import {defineStore} from 'pinia';

/**
 * Parse PostgREST-style filter tokens (eq., in.(), is.true, …).
 * @param {string} token
 * @returns {Record<string, unknown>|null}
 */
function parseApiTokenToOperators(token) {
  if (token === undefined || token === null) {
    return null;
  }
  const t = String(token).trim();
  if (!t) {
    return null;
  }
  if (t === 'is.true' || t === 'true') {
    return {'=': true};
  }
  if (t === 'not.is.true' || t === 'false') {
    return {'=': false};
  }
  if (t.startsWith('eq.')) {
    return {'=': t.slice(3)};
  }
  if (t.startsWith('in.(') && t.endsWith(')')) {
    const values = t
      .slice(4, -1)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    if (values.length === 0) {
      return null;
    }
    return {'∈': values};
  }
  return null;
}

/**
 * Build filter object shape expected by HitFloatingPanel.formatFiltersForNeighborProcedure
 * (propertyName -> { op -> value }).
 */
function buildFiltersObject({filterIdColumn, filterId, customFilters}) {
  const operatorMap = {};
  if (filterIdColumn && filterId !== undefined && filterId !== null) {
    operatorMap[filterIdColumn] = {'=': filterId};
  }
  if (customFilters && typeof customFilters === 'object') {
    Object.entries(customFilters).forEach(([column, token]) => {
      if (typeof token !== 'string') {
        return;
      }
      const operators = parseApiTokenToOperators(token);
      if (!operators) {
        return;
      }
      operatorMap[column] = {...(operatorMap[column] ?? {}), ...operators};
    });
  }
  return operatorMap;
}

const NEIGHBOR_TABBED_LIST_PANELS = [
  {
    apiRoutes: ['project', 'project_part'],
    panelId: 'project.list',
    selectedTabConfigKey: 'project.list.selected-tab',
    defaultTab: 'projects',
    fixedSuffixByApiRoute: {project: 'projects'},
  },
  {
    apiRoutes: ['transaction'],
    panelId: 'transaction.list',
    selectedTabConfigKey: 'transaction.list.selected-tab',
    defaultTab: 'all-transactions',
  },
  {
    apiRoutes: ['action'],
    panelId: 'action.list',
    selectedTabConfigKey: 'action.list.selected-tab',
    defaultTab: 'list',
  },
];

function resolveNeighborTabbedListSuffix(entry, apiRoute, getConfiguration) {
  const fixed = entry.fixedSuffixByApiRoute?.[apiRoute];
  if (fixed != null && fixed !== '') {
    return fixed;
  }
  return getConfiguration(entry.selectedTabConfigKey) ?? entry.defaultTab;
}

/**
 * @param {string} apiRoute
 * @param {{ getConfiguration: (label: string) => unknown }} configurationStore
 * @returns {{ panelId: string, suffix: string } | null}
 */
export function getNeighborTabbedListPanelContext(
  apiRoute,
  configurationStore
) {
  if (!apiRoute || !configurationStore?.getConfiguration) {
    return null;
  }
  const getConfiguration = (key) => configurationStore.getConfiguration(key);
  for (const entry of NEIGHBOR_TABBED_LIST_PANELS) {
    if (!entry.apiRoutes.includes(apiRoute)) {
      continue;
    }
    return {
      panelId: entry.panelId,
      suffix: resolveNeighborTabbedListSuffix(
        entry,
        apiRoute,
        getConfiguration
      ),
    };
  }
  return null;
}

/**
 * Remembers the last list/panel scope when the user opens a detail from a HitContainerTable
 * (filter column + filter id + optional customFilters). Used by HitFloatingPanel for
 * previous/next without putting filters in the URL.
 */
export const useNeighborNavigationStore = defineStore(
  'neighborNavigationContext',
  {
    state: () => ({
      /**
       * @type {Record<string, {
       *   filterIdColumn?: string,
       *   filterId?: string,
       *   customFilters?: object,
       *   filtersObject: object,
       *   sorting?: { property: string, order: string } | null,
       *   updatedAt: number
       * }>}
       */
      contextsByApiRoute: {},
    }),
    actions: {
      /**
       * Call on each navigation from a container-backed table to a detail route.
       * @param {{ apiRoute: string, filterIdColumn?: string, filterId?: string, customFilters?: object, sorting?: { property?: string, order?: string } }} payload
       */
      setFromContainerContext(payload) {
        const {apiRoute, filterIdColumn, filterId, customFilters, sorting} =
          payload || {};
        if (!apiRoute) {
          return;
        }
        const filtersObject = buildFiltersObject({
          filterIdColumn,
          filterId,
          customFilters,
        });
        const sortingSnapshot =
          sorting?.property && sorting?.order
            ? {property: sorting.property, order: sorting.order}
            : null;
        this.contextsByApiRoute[apiRoute] = {
          filterIdColumn,
          filterId,
          customFilters: customFilters ? {...customFilters} : {},
          filtersObject,
          sorting: sortingSnapshot,
          updatedAt: Date.now(),
        };
      },
      clearApiRoute(apiRoute) {
        if (apiRoute && this.contextsByApiRoute[apiRoute]) {
          delete this.contextsByApiRoute[apiRoute];
        }
      },
      clearAll() {
        this.contextsByApiRoute = {};
      },
    },
    getters: {
      getFiltersObjectForApiRoute: (state) => (apiRoute) => {
        return state.contextsByApiRoute[apiRoute]?.filtersObject ?? null;
      },
      /**
       * Table sort active when the user left the list (neighbor scope). Same shape as
       * configuration store getTableSorting: { property, order }.
       */
      getTableSortingForApiRoute: (state) => (apiRoute) => {
        const sorting = state.contextsByApiRoute[apiRoute]?.sorting;
        if (!sorting?.property || !sorting?.order) {
          return null;
        }
        return sorting;
      },
    },
  }
);
